import { Core, Decorative } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { MediumFormatting } from "@faire/web--source/common/localization/formatting";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackFaireDirectCreditHcArticleFdLandingPageView } from "@faire/web-api--source/events/faireDirect/view/creditHcArticleFdLandingPage";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import brandPath from "@faire/web-api--source/routes/brand/brandToken";
import helpCenterArticles from "@faire/web-api--source/routes/support/articles/articleId";
import { observer } from "mobx-react";
import * as React from "react";
import { useEffect, type JSX } from "react";
import styled from "styled-components";

import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { getExternalSalesRepName } from "@faire/retailer/serialized-data/getExternalSalesRepName";
import { getSettingFaireDirectCreditExpiresInDays } from "@faire/retailer/settings/getSettingFaireDirectCreditExpiresInDays";
import {
  useSettingFirstOrderIncetiveForFaireDirectLoggedOut,
  useAssignSettingFirstOrderIncetiveForFaireDirectLoggedOut,
} from "@faire/retailer/settings/useSettingFirstOrderIncetiveForFaireDirectLoggedOut";
import { SignIn } from "@faire/retailer/views/faire-direct/components/SignIn";

import { NarrowContainer } from "../components/CommonStyles";
import { FaireAndBrandLogo } from "../components/FaireAndBrandLogo";
import { SignUpButton } from "../components/SignUpButton";

interface IProps {
  brand: IBrand;
  isPreSample: boolean;
  innerRef?: React.MutableRefObject<HTMLDivElement | null>;
  loginUrl?: string;
}

const IntroSectionBase: React.FunctionComponent<IProps> = ({
  brand,
  isPreSample,
  innerRef,
  loginUrl,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const viewport = useViewport();

  const signUpSection = (
    <Column align="center" ref={innerRef}>
      <SignUpButton primary data-test-id="faire-direct-sign-up-button-main" />
    </Column>
  );

  const externalSalesRepName = getExternalSalesRepName();
  const {
    isFaireDirectCreditPercentageBased,
    formattedMaxPercentageDiscountAmount,
  } = useFaireDirectFirstOrderIncentive();

  const { faireDirectBrandToken } = useFaireDirectBrand();

  const getGreetingContent = () => {
    if (externalSalesRepName) {
      return strictLocalize(
        {
          defaultMessage:
            "{salesRepName} invited you to shop {brandName} on Faire!",
        },
        DEPRECATED_backwardsCompatibleValues({
          salesRepName: externalSalesRepName,
          brandName: brand.name,
        })
      );
    }
    if (!isFaireDirectCreditPercentageBased) {
      return strictLocalize({
        defaultMessage:
          "We’ve partnered with Faire to sell our wholesale products online.",
      });
    }
    return null;
  };

  const getDisclaimer = () => {
    if (isFaireDirectCreditPercentageBased) {
      return (
        <StrictLocalMsg
          defaultMessage="*Available for retailers who are new to Faire. Maximum discount of {maximumDiscountMoney} and free shipping with <BrandPageLinkFormatting>{brandCompanyName}</BrandPageLinkFormatting> only. Expires {expiresInDaysNumber} days after retailer signs up for Faire."
          description={{
            text: "It's the disclaimer for the Faire Direct Signup Credit offer",
            img: "https://cdn.faire.com/fastly/32b4354d622c0a0e2804c889ed198e1f5abc70198f47634b69438651bd9b06ad.png",
          }}
          values={{
            brandCompanyName: brand.name ?? "",
            maximumDiscountMoney: formattedMaxPercentageDiscountAmount,
            expiresInDaysNumber: getSettingFaireDirectCreditExpiresInDays(),
            BrandPageLinkFormatting: (chunks) =>
              BrandPageLinkFormatting(chunks, faireDirectBrandToken),
          }}
        />
      );
    }
    if (brand.name) {
      return (
        <StrictLocalMsg
          defaultMessage="*You must be new to Faire and meet the minimum for <BrandPageLinkFormatting>{brandCompanyName}</BrandPageLinkFormatting>. No other conditions apply. <LinkFormatting>Learn More</LinkFormatting>"
          description={{
            text: "Stating the only 2 conditions that have to be met for this offer with a link to an article",
            img: "https://cdn.faire.com/fastly/077cdfbe82597aee4b72585cbdbd46e975cf5ef23162aecc73ffbbada7755a91.png",
          }}
          values={{
            brandCompanyName: brand.name,
            LinkFormatting: HelpCenterLinkFormatting,
            BrandPageLinkFormatting: (chunks) =>
              BrandPageLinkFormatting(chunks, faireDirectBrandToken),
          }}
        />
      );
    } else {
      return (
        <StrictLocalMsg
          defaultMessage="*You must be new to Faire and meet the minimum for the brand. No other conditions apply. <LinkFormatting>Learn More</LinkFormatting>"
          description={{
            text: "Stating the only 2 conditions that have to be met for this offer with a link to an article",
            img: "https://cdn.faire.com/fastly/077cdfbe82597aee4b72585cbdbd46e975cf5ef23162aecc73ffbbada7755a91.png",
          }}
          values={{
            LinkFormatting: HelpCenterLinkFormatting,
          }}
        />
      );
    }
  };

  return (
    <>
      {viewport.isMobile && !getIsBrandPreview() && !isPreSample ? (
        <>
          <MobileSignInContainer>
            <CenterRow>
              <SignIn
                loginUrl={loginUrl}
                eventPath="fd-landing-page/click/log-in"
              />
            </CenterRow>
          </MobileSignInContainer>
          <HorizontalLine />
        </>
      ) : null}
      <Container>
        <IntroContainer>
          <Spacer size={viewport.isMobile ? "3x" : "8x"} />
          <FaireAndBrandLogo brandName={brand.name || ""} />
          <Spacer size="3x" />
          {viewport.isMobile ? (
            <Typography variant="displayMSerifRegular" align="center">
              <StrictLocalMsg
                defaultMessage="Shop {givenCompanyName} online"
                description={{
                  text: "Title of Faire Direct landing page encouraging retailers to shop from that Brand",
                  img: "https://cdn.faire.com/fastly/8eb2042c4e4eb90b4271ddb4dfa04af40da7204b8507c73862907b4f51ceaec6.png",
                }}
                values={{
                  givenCompanyName: brand.name ?? "",
                }}
              />
            </Typography>
          ) : (
            <Typography variant="displayLSerifRegular" align="center">
              <StrictLocalMsg
                defaultMessage="Shop <NonBreakingFormatting>{brandName}</NonBreakingFormatting> online"
                description={{
                  text: "Title of Faire Direct landing page encouraging retailers to shop from that Brand",
                  img: "https://cdn.faire.com/fastly/8eb2042c4e4eb90b4271ddb4dfa04af40da7204b8507c73862907b4f51ceaec6.png",
                }}
                values={DEPRECATED_backwardsCompatibleValues({
                  brandName: brand.name,
                  NonBreakingFormatting: NonBreakingFormatting,
                })}
              />
            </Typography>
          )}
          <InnerContainer>
            <Spacer size="2x" />
            <Typography
              variant={{
                mobileAndAbove: "paragraphSansRegular",
                tabletAndAbove: "sectionHeaderSansMedium",
              }}
              align="center"
            >
              {getGreetingContent()}
            </Typography>
            <Typography
              variant={{
                mobileAndAbove: "paragraphSansRegular",
                tabletAndAbove: "sectionHeaderSansMedium",
              }}
              align="center"
            >
              <DiscountMessageContent />
            </Typography>
            <Spacer
              size={{
                mobileAndAbove: 24,
                tabletAndAbove: 40,
              }}
            />
            {signUpSection}
            <Spacer
              size={{
                mobileAndAbove: 16,
                tabletAndAbove: 28,
              }}
            />
            <Typography
              variant="labelSansRegular"
              color={Core.text.subdued}
              align="center"
            >
              {getDisclaimer()}
            </Typography>
            {viewport.isMobile ? <Spacer size="3x" /> : null}
          </InnerContainer>
        </IntroContainer>
      </Container>
    </>
  );
};

export const IntroSection = observer(IntroSectionBase);

const DiscountMessageContent: React.FC = () => {
  const {
    isFaireDirectCreditPercentageBased,
    formattedMaxPercentageDiscountAmount,
    formattedFixedBasedAmount,
    formattedPercentageBasedNumber,
  } = useFaireDirectFirstOrderIncentive();

  const isFoiForFD = useSettingFirstOrderIncetiveForFaireDirectLoggedOut();
  const assignFoiForFD =
    useAssignSettingFirstOrderIncetiveForFaireDirectLoggedOut();

  useEffect(() => {
    assignFoiForFD();
  }, [assignFoiForFD]);

  if (isFoiForFD) {
    return isFaireDirectCreditPercentageBased ? (
      <StrictLocalMsg
        defaultMessage="Join today to claim your referral offer: {faireDirectPercentageDiscountNumber} off your first order, up to {formattedMaxPercentageDiscountMoney}, and 1 year of free shipping.*"
        description={{
          text: "Subheader text for logged-out brand page",
          img: "https://cdn.faire.com/fastly/f0049a2235f123f67fb8393a0b6a4bd055621a85c6a086c5b576feddd5af5767.png",
        }}
        values={{
          faireDirectPercentageDiscountNumber: formattedPercentageBasedNumber,
          formattedMaxPercentageDiscountMoney:
            formattedMaxPercentageDiscountAmount,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Join today to claim your referral offer: <MediumFormatting>{discountMoney} off</MediumFormatting> your first order, and <MediumFormatting>1 year of free shipping</MediumFormatting>.*"
        description={{
          text: "Subheader text for logged-out brand page",
          img: "https://cdn.faire.com/fastly/3e268c3985abe3e34a3a649f8819f3b13f070b1d5138916228bc45478178aa09.png",
        }}
        values={{
          MediumFormatting: MediumFormatting,
          discountMoney: formattedFixedBasedAmount,
        }}
      />
    );
  }

  return isFaireDirectCreditPercentageBased ? (
    <StrictLocalMsg
      defaultMessage="Join today and get {faireDirectPercentageDiscountNumber} off on your first order (up to {maximumDiscountMoney}) and 1 year of free shipping—covered by Faire.*"
      description={{
        text: "Promotional message stating that said benefits will be covered by Faire. It's on the Faire Direct retailer signup page",
        img: "https://cdn.faire.com/fastly/32b4354d622c0a0e2804c889ed198e1f5abc70198f47634b69438651bd9b06ad.png",
      }}
      values={{
        faireDirectPercentageDiscountNumber: formattedPercentageBasedNumber,
        maximumDiscountMoney: formattedMaxPercentageDiscountAmount,
      }}
    />
  ) : (
    <StrictLocalMsg
      defaultMessage="Join today and get <MediumFormatting>{discountMoney} off</MediumFormatting> and <MediumFormatting>1 year of free shipping</MediumFormatting>, covered by Faire.*"
      description={{
        text: "Promotional message stating that said benefits will be covered by Faire",
        img: "https://cdn.faire.com/fastly/077cdfbe82597aee4b72585cbdbd46e975cf5ef23162aecc73ffbbada7755a91.png",
      }}
      values={{
        MediumFormatting: MediumFormatting,
        discountMoney: formattedFixedBasedAmount,
      }}
    />
  );
};

const BrandPageLinkFormatting = (
  chunks: JSX.Element,
  brandToken: string | undefined
) => <Link to={brandPath(brandToken ?? "")}>{chunks}</Link>;

const HelpCenterLinkFormatting = (chunks: JSX.Element) => (
  <EmbeddedLink
    linkTo={helpCenterArticles("360034300092")}
    onClick={trackFaireDirectCreditHcArticleFdLandingPageView}
    target="_blank"
  >
    {chunks}
  </EmbeddedLink>
);

const NonBreakingFormatting = (chunks: JSX.Element) => (
  <div
    style={{
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
    }}
  >
    {chunks}
  </div>
);

const MobileSignInContainer = styled.div`
  width: 100%;
  background: ${Decorative.tint.neutral};
  padding: 9px 0;
  align-items: center;
`;

const Container = styled(Column).attrs({ align: "center" })`
  width: 100%;
  background: ${Decorative.tint.neutral};
  padding: 20px;
  padding-bottom: 220px;
`;

const IntroContainer = styled(NarrowContainer)`
  align-items: center;
  max-width: 930px;
`;

const InnerContainer = styled(Column)`
  max-width: 740px;
  padding: 0 15px;
`;

const CenterRow = styled(Row)`
  align-self: center;
  margin: auto;
  width: fit-content;
`;
